'use client';

import { hashOrganizationId } from '@/lib/analytics/googleAnalytics';

// Lazy load Sentry to avoid webpack warnings
let Sentry: typeof import('@sentry/nextjs') | null = null;

if (typeof window !== 'undefined') {
  import('@sentry/nextjs').then((module) => {
    Sentry = module;
  }).catch(() => {
    console.warn('[Sentry] Failed to load Sentry SDK');
  });
}

/**
 * Initialize Sentry with environment-based configuration
 */
export function initSentry(): void {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const environment = process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const release = process.env.SENTRY_RELEASE || 'unknown';
  
  if (!dsn || dsn === 'your-sentry-dsn-here') {
    console.warn('[Sentry] Not configured - error tracking disabled');
    return;
  }

  // Sentry is initialized in sentry.client.config.ts with environment-based settings
  console.log(`[Sentry] Configuration loaded for ${environment} environment (release: ${release})`);
}

/**
 * Get current Sentry environment
 */
export function getSentryEnvironment(): string {
  return process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development';
}

/**
 * Check if Sentry is configured for production
 */
export function isSentryProduction(): boolean {
  const environment = getSentryEnvironment();
  return environment === 'production';
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(
  userId: string,
  organizationId: string,
  role: string,
  tier: string
): void {
  if (!Sentry) return;
  
  try {
    Sentry.setUser({
      id: userId,
      organization_id_hash: hashOrganizationId(organizationId),
      role,
      tier,
    });
  } catch (error) {
    console.warn('[Sentry] Failed to set user context:', error);
  }
}

/**
 * Clear user context
 */
export function clearSentryUser(): void {
  if (!Sentry) return;
  
  try {
    Sentry.setUser(null);
  } catch (error) {
    console.warn('[Sentry] Failed to clear user context:', error);
  }
}

/**
 * Capture error with context
 */
export function captureError(
  error: Error,
  context?: Record<string, any>
): void {
  if (!Sentry) return;
  
  try {
    Sentry.captureException(error, {
      extra: context,
    });
  } catch (err) {
    console.warn('[Sentry] Failed to capture error:', err);
  }
}

/**
 * Capture RLS policy violation
 */
export function captureRLSViolation(
  error: Error,
  table: string,
  operation: string,
  userId: string
): void {
  if (!Sentry) return;
  
  try {
    Sentry.captureException(error, {
      tags: {
        error_type: 'rls_violation',
        table,
        operation,
      },
      extra: {
        user_id: userId,
        error_code: '42501',
      },
    });
  } catch (err) {
    console.warn('[Sentry] Failed to capture RLS violation:', err);
  }
}

/**
 * Capture Stripe payment failure
 */
export function captureStripeError(
  error: Error,
  paymentIntentId?: string,
  amount?: number,
  currency?: string
): void {
  if (!Sentry) return;
  
  try {
    Sentry.captureException(error, {
      tags: {
        error_type: 'stripe_payment_failure',
      },
      extra: {
        payment_intent_id: paymentIntentId,
        amount,
        currency,
      },
    });
  } catch (err) {
    console.warn('[Sentry] Failed to capture Stripe error:', err);
  }
}

/**
 * Add breadcrumb for user action
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, any>
): void {
  if (!Sentry) return;
  
  try {
    Sentry.addBreadcrumb({
      message,
      category,
      level: 'info',
      data,
    });
  } catch (err) {
    console.warn('[Sentry] Failed to add breadcrumb:', err);
  }
}